/** Label sent to the API when the clinician selects the general care plan (PHA-466). */
export const GENERAL_CARE_PLAN_SELECTION_LABEL = 'General care plan';

export interface CarePlanMatchOption {
  display_name: string;
}

/**
 * Build the list of selectable care plan options shown to the clinician.
 */
export function buildCarePlanSelectionOptions(
  matchedDiseases: CarePlanMatchOption[],
  suggestGeneral: boolean
): string[] {
  const options = matchedDiseases.map((d) => d.display_name);
  if (suggestGeneral) {
    options.push(GENERAL_CARE_PLAN_SELECTION_LABEL);
  }
  return options;
}

/**
 * PHA-1036: never skip the picker.
 *
 * PHA-707/PHA-809 used to auto-generate without showing the picker when one or two
 * diagnoses matched, on the reasoning that APCM Tier 2 / CCM need two diagnoses so
 * there was nothing to decide. There is: the clinician has to be aware of which Dx
 * the plan is being built for, and has to be able to decide NOT to create one when
 * that is the right call. Auto-generating took both away and did it silently.
 *
 * Kept as a named function rather than deleted so the intent stays greppable, and
 * so the two call sites keep reading as "should we auto-generate?" rather than
 * quietly losing the concept.
 */
export function shouldAutoGenerateCarePlanSelection(_optionCount: number): boolean {
  return false;
}

/**
 * Initial checkbox state for a first-time care plan: nothing selected.
 *
 * PHA-707 ("do not pre-select care plans - make it a hard stop for the clinicians
 * to decide") is the rule here. The care-plan REFRESH flow is the exception and
 * uses getRefreshInitialSelectedDiseases instead.
 */
export function getInitialSelectedDiseases(
  _matchedDiseases: CarePlanMatchOption[],
  _suggestGeneral: boolean
): Set<string> {
  return new Set();
}

/**
 * PHA-1036: always null — the clinician always chooses. Retained so the call site
 * keeps its shape and the behaviour is switched in exactly one place.
 */
export function getAutoGenerateCarePlanDiseases(
  _matchedDiseases: CarePlanMatchOption[],
  _suggestGeneral: boolean
): string[] | null {
  return null;
}

/**
 * PHA-1047: initial checkbox state for a care-plan REFRESH — the diagnoses the
 * patient's CURRENT approved plan already focuses on, pre-checked and editable.
 *
 * Read the PHA-908 warning on getRegenerateDiseaseSelection below before changing
 * this. An explicit `selected_diseases` list is honoured verbatim server-side, so
 * copying the old plan's conditions blindly can resurrect a problem the clinician
 * just removed. This is safe because it only ever pre-checks options that are
 * ALREADY in `matchedDiseases` — and `matchedDiseases` comes from matching against
 * the problem list the clinician just approved in the Review step. A condition
 * whose problem was rejected on the way in is therefore not offered at all, so it
 * cannot be pre-checked.
 *
 * A newly matched condition that the old plan did not cover is deliberately left
 * UNCHECKED: adding a condition to the plan is a new clinical decision, not a
 * continuation of the previous one.
 */
export function getRefreshInitialSelectedDiseases(
  matchedDiseases: CarePlanMatchOption[],
  suggestGeneral: boolean,
  currentPlanDiseases: string[]
): Set<string> {
  const options = buildCarePlanSelectionOptions(matchedDiseases, suggestGeneral);
  const wanted = new Set(
    (currentPlanDiseases || [])
      .map((d) => (d || '').trim())
      .filter((d) => d && !isMergedCarePlanSentinel(d))
      .map((d) =>
        isGeneralCarePlanSelectionDisease(d)
          ? GENERAL_CARE_PLAN_SELECTION_LABEL.toLowerCase()
          : d.toLowerCase()
      )
  );
  return new Set(options.filter((option) => wanted.has(option.toLowerCase())));
}

/**
 * Backend marker for a merged multi-condition plan (MERGED_CARE_PLAN_DISEASE_SENTINEL).
 * It is a parent identifier, NOT a disease name — it matches no care plan template.
 */
export const MERGED_CARE_PLAN_DISEASE_SENTINEL = 'global';

/**
 * Diseases to use when re-generating without an explicit checkbox selection
 * (e.g. after review-tab changes). Prefer the last generated plan, not all matches.
 *
 * PHA-908: the normal generation path produces ONE merged plan whose `disease` is the
 * 'global' sentinel — a parent identifier, not a condition. Re-sending it verbatim made
 * the backend reject it as an unknown disease and trapped the care team in a loop.
 *
 * Merged plans are dropped from the result rather than being resolved to the conditions
 * they happen to list. An EMPTY result means "no explicit selection", which makes the
 * backend re-match against the CURRENT approved chart — the right answer for the workflow
 * this bug came from, where the clinician has just edited problems/medications. Resolving
 * the stale plan's own conditions instead would resurrect a problem they just removed,
 * because an explicit selection is honored verbatim server-side (PHA-638).
 */
export function getRegenerateDiseaseSelection(
  selectedDiseases: string[],
  carePlans: (CarePlanClinicalDiseaseSource & { disease?: string })[] | undefined,
  matchedDiseases: CarePlanMatchOption[],
  suggestGeneral: boolean
): string[] {
  if (selectedDiseases.length > 0) {
    return selectedDiseases;
  }
  if (!carePlans || carePlans.length === 0) {
    return [];
  }

  const seen = new Set<string>();
  const out: string[] = [];
  const add = (raw: string | undefined) => {
    const n = (raw || '').trim();
    if (!n) return;
    const key = n.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(n);
  };

  for (const plan of carePlans) {
    const disease = (plan.disease || '').trim();
    if (!disease || isMergedCarePlanSentinel(disease)) continue;
    add(
      isGeneralCarePlanSelectionDisease(disease)
        ? GENERAL_CARE_PLAN_SELECTION_LABEL
        : disease
    );
  }
  return out;
}

/**
 * PHA-1157: the conditions to send when re-generating or resetting a plan that was generated
 * from an explicit picker selection.
 *
 * The checkbox state alone is not a safe source: the disease-match loader re-runs while a
 * generation is in flight and re-seeds the checkboxes (to the refresh pre-selection, or to
 * nothing), so by the time "Reset to standard guidelines" is clicked the clinician's pick is
 * gone. With an empty selection the fallback drops the merged 'global' plan and the server
 * re-matches the whole chart, which silently adds conditions the clinician unchecked.
 * The selection the last generation actually used wins whenever one was recorded.
 */
export function resolveRegenerateSelection(
  lastGenerationSelection: string[] | null | undefined,
  selectedDiseases: string[],
  carePlans: (CarePlanClinicalDiseaseSource & { disease?: string })[] | undefined,
  matchedDiseases: CarePlanMatchOption[],
  suggestGeneral: boolean
): string[] {
  if (lastGenerationSelection && lastGenerationSelection.length > 0) {
    return [...lastGenerationSelection];
  }
  return getRegenerateDiseaseSelection(selectedDiseases, carePlans, matchedDiseases, suggestGeneral);
}

function isGeneralCarePlanSelectionDisease(disease: string): boolean {
  const normalized = disease.trim().toLowerCase();
  return normalized === 'general' || normalized === 'general care plan';
}

function isMergedCarePlanSentinel(disease: string): boolean {
  return disease.trim().toLowerCase() === MERGED_CARE_PLAN_DISEASE_SENTINEL;
}

export interface CarePlanClinicalDiseaseSource {
  /**
   * The plan's own condition: a per-condition plan carries it here ('Hypertension'), a merged
   * plan carries the 'global' sentinel. Per-condition plans often have an EMPTY `diseases`
   * rollup and untagged items, so this is their only marker (PHA-1227).
   */
  disease?: string;
  icd10_codes?: { code?: string; clinical_disease?: string }[];
  goals?: { diseases?: string[] }[];
  questions?: { diseases?: string[] }[];
  tasks?: { diseases?: string[] }[];
  /**
   * PHA-340 rollup of the conditions this plan covers, with the 'global' sentinel already
   * expanded into real labels. Present on the patient payload; `icd10_codes` is NOT
   * (`patients_with_details` does not select it), so for anything reading a plan off
   * `patient.care_plan_definitions` this is the field that actually carries the answer.
   */
  diseases?: string[];
  /** True when the plan is the no-curated-Dx "General care plan". */
  is_general?: boolean;
}

/** Unique clinical condition names attributed on a generated care plan. */
export function getClinicalDiseasesFromCarePlan(plan: CarePlanClinicalDiseaseSource): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const add = (raw: string | undefined) => {
    const n = (raw || '').trim();
    if (!n) return;
    const key = n.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(n);
  };
  // PHA-1227: a per-condition plan names its condition only here; skip the merged sentinel.
  if (plan.disease && !isMergedCarePlanSentinel(plan.disease)) add(plan.disease);
  for (const c of plan.icd10_codes || []) add(c.clinical_disease);
  // PHA-1047 fix: the PHA-340 rollup. Without it a plan read off the patient payload
  // contributes only whatever its goals/questions/tasks happen to name, because
  // `icd10_codes` is absent from that payload entirely.
  for (const d of plan.diseases || []) add(d);
  for (const g of plan.goals || []) for (const d of g.diseases || []) add(d);
  for (const q of plan.questions || []) for (const d of q.diseases || []) add(d);
  for (const t of plan.tasks || []) for (const d of t.diseases || []) add(d);
  // A general plan names no condition anywhere in the payload — its only marker is the
  // flag — so without this a no-Dx plan can never pre-select its own option.
  if (plan.is_general) add(GENERAL_CARE_PLAN_SELECTION_LABEL);
  return out;
}

/**
 * The union of clinical conditions across a set of care plans, de-duplicated
 * case-insensitively and in first-seen order.
 *
 * PHA-1119 follow-up: extracted because "what does this patient's current plan cover?" now has
 * two sources. For an enrolled patient it is `patient.care_plan_definitions` (approved only);
 * for a patient still mid-onboarding, whose plan exists solely as an unapproved draft, it is
 * the draft batch — and both have to be read the same way for the condition checkboxes to
 * pre-tick consistently. See OnboardingWorkflow's refresh entry.
 */
export function collectClinicalDiseasesAcrossPlans(
  plans: CarePlanClinicalDiseaseSource[] | null | undefined
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const plan of plans || []) {
    for (const disease of getClinicalDiseasesFromCarePlan(plan)) {
      const key = disease.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(disease);
    }
  }
  return out;
}

/**
 * Diseases chosen for care plan generation when available; otherwise infer from
 * the generated plan (resume draft without selection state).
 */
export function resolveGenerationSelectedDiseases(
  selectedDiseases: string[],
  carePlans: CarePlanClinicalDiseaseSource[]
): string[] {
  if (selectedDiseases.length > 0) {
    return selectedDiseases;
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const plan of carePlans) {
    for (const d of getClinicalDiseasesFromCarePlan(plan)) {
      const key = d.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(d);
    }
  }
  return out;
}

/** Condition dropdown options for ICD-10 custom/quick-add (generation scope only). */
export function getClinicalDiseaseOptionsForPlan(
  plan: CarePlanClinicalDiseaseSource,
  generationSelectedDiseases: string[]
): string[] {
  const fromSelected = generationSelectedDiseases
    .map((d) => d.trim())
    .filter((d) => d && !isGeneralCarePlanSelectionDisease(d));

  const scope =
    fromSelected.length > 0
      ? fromSelected
      : getClinicalDiseasesFromCarePlan(plan).filter(
          (d) => !isGeneralCarePlanSelectionDisease(d)
        );

  const scopeKeys = new Set(scope.map((d) => d.toLowerCase()));
  const seen = new Set<string>();
  const out: string[] = [];
  const add = (raw: string | undefined) => {
    const n = (raw || '').trim();
    if (!n || isGeneralCarePlanSelectionDisease(n)) return;
    if (scopeKeys.size > 0 && !scopeKeys.has(n.toLowerCase())) return;
    const key = n.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(n);
  };

  for (const d of scope) add(d);
  for (const c of plan.icd10_codes || []) add(c.clinical_disease);
  return out;
}
